import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import LicenseInvoiceFollowUpStatusSelect from "@/v2-backoffice-client/license/collections/LicenseInvoiceFollowUpStatusSelect"
import { useInvoiceFollowUpUpsertMutation } from "@/v2-backoffice-client/license/collections/license.invoiceFollowUp.upsert.mutation"
import { buildFinanceHandoffMailto } from "@/v2-backoffice-client/license/collections/license.collections.utils"
import {
  evictInvoiceCollectionQueries,
  dateInputToDatetime,
} from "@/v2-backoffice-client/license/license.utils"
import {
  INVOICE_FOLLOWUP_STATUS_CONTACTED,
  INVOICE_FOLLOWUP_STATUS_ISSUE,
} from "@/v2-backoffice-client/license/license.constants"
import type { InvoiceCollectionsQuery } from "@/v2-backoffice-client/license/collections/__types__/license.collections.query"

export type LicenseInvoiceFollowUpDialogProps = {
  invoice: InvoiceCollectionsQuery["getInvoiceCollections"][number]
  open: boolean
  onOpenChange(open: boolean): void
}

// The dunning row editor: where the conversation with ONE debtor stands
// (status), when they promised to pay, and the paper trail (note). The first
// save starts the follow-up — the row leaves "Not started". Deliberately NOT
// where money truth lives: payments land in Fenerum every morning and the
// hourly mirror resolves the row to Paid on its own.
function LicenseInvoiceFollowUpDialog(
  props: LicenseInvoiceFollowUpDialogProps,
) {
  const { invoice, open, onOpenChange } = props

  const [statusCode, setStatusCode] = useState(
    String(
      invoice.InvoiceFollowUpStatusCode ?? INVOICE_FOLLOWUP_STATUS_CONTACTED,
    ),
  )
  const [promisedAt, setPromisedAt] = useState(
    invoice.PromisedPaymentAt
      ? String(invoice.PromisedPaymentAt).slice(0, 10)
      : "",
  )
  const [note, setNote] = useState(invoice.Note ?? "")

  const debtorName =
    invoice.CustomerName ?? invoice.AccountCompanyName ?? "the customer"

  // Saving INTO "Issue – sent to Finance" opens the handoff draft (arbo@,
  // customer name as subject, full context in the body) so the status and
  // the email can't drift apart — the email was the forgotten half of the
  // manual process. Only on the transition, not on every re-save; a human
  // still reads and sends it (C5).
  const isNewIssue =
    Number(statusCode) === INVOICE_FOLLOWUP_STATUS_ISSUE &&
    invoice.InvoiceFollowUpStatusCode !== INVOICE_FOLLOWUP_STATUS_ISSUE

  const [upsertFollowUp, { loading, error }] = useInvoiceFollowUpUpsertMutation(
    {
      update: evictInvoiceCollectionQueries,
      onCompleted() {
        toast({
          variant: "success",
          title: "Follow-up updated",
          description: isNewIssue
            ? "Saved — the Finance handoff draft is opening in your mail client. Read it and send it."
            : `The follow-up on invoice ${invoice.InvoiceNumber ?? invoice.InvoiceId} was saved.`,
        })
        if (isNewIssue) {
          window.location.href = buildFinanceHandoffMailto(
            invoice,
            note.trim() ? note.trim() : null,
          )
        }
        onOpenChange(false)
      },
    },
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            Follow-up — invoice {invoice.InvoiceNumber ?? invoice.InvoiceId},{" "}
            {debtorName}
          </Dialog.Title>
          <Dialog.Description>
            Track the payment conversation here. Money truth stays in Fenerum:
            payments land there every morning and this row resolves itself to
            Paid — nobody hand-marks anything.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Status</Label>
            <LicenseInvoiceFollowUpStatusSelect
              value={statusCode}
              onValueChange={setStatusCode}
            />
          </div>

          <div className="space-y-2">
            <Label>Promised payment date</Label>
            <Input
              type="date"
              value={promisedAt}
              onChange={(e) => {
                setPromisedAt(e.target.value)
              }}
            />
            <p className="text-xs text-muted-foreground">
              When the customer said they would pay — the board flags broken
              promises once the date passes.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={note}
              rows={3}
              placeholder="What the customer said, what was agreed..."
              onChange={(e) => {
                setNote(e.target.value)
              }}
            />
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            data-feature-code="UI_BackofficeCollections_FollowUp_Save"
            onClick={() => {
              void upsertFollowUp({
                variables: {
                  input: {
                    InvoiceId: invoice.InvoiceId,
                    InvoiceFollowUpStatusCode: Number(statusCode),
                    PromisedPaymentAt: promisedAt
                      ? dateInputToDatetime(promisedAt)
                      : null,
                    Note: note.trim() ? note.trim() : null,
                  },
                },
              })
            }}
          >
            {loading ? "Saving..." : "Save Follow-up"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseInvoiceFollowUpDialog
